import type { DropDownListChangeEvent } from "@tql/ui-components-react/kendo/dropdowns";
import type {
	GridCustomCellProps,
	GridItemChangeEvent,
} from "@tql/ui-components-react/kendo/grid";
import { DropDownList } from "@tql/ui-components-react/tql/dropdowns";

import { GridTd } from "~/shared/components/trax-grid/grid-td/grid-td.tsx";
import type {
	RoutingGuideEnumItem,
	RoutingGuideEnumsType,
} from "~/shared/schemas/routing-guide-schema.ts";

import { getOperators } from "../../../modal/utils/get-operators.ts";
import type {
	CarrierRule,
	RowUpdate,
	Rule,
} from "../../../modal/utils/types.ts";

type OperatorCellProps<T extends Rule | CarrierRule> = GridCustomCellProps & {
	handleRowChange: (event: GridItemChangeEvent | RowUpdate<T>) => void;
	isReadOnly: boolean;
	routingGuideEnums?: RoutingGuideEnumsType;
	showActivationError?: boolean;
	cellTestId?: string;
};

export const OperatorCell = <T extends Rule | CarrierRule>({
	handleRowChange,
	isReadOnly,
	routingGuideEnums,
	showActivationError,
	...props
}: OperatorCellProps<T>) => {
	const dataItem = props.dataItem as T;
	const formattedOperators = getOperators(
		dataItem.fieldSelector ?? "",
		routingGuideEnums,
	);
	const selectedOperator =
		formattedOperators.find((c) => c.name === dataItem.operator) ?? null;

	const isDisabled = !dataItem.fieldSelector || isReadOnly;
	const hasError =
		showActivationError === true && !isDisabled && !dataItem.operator;

	return (
		<GridTd data-testid={props.cellTestId} tdProps={props.tdProps}>
			<div
				style={
					hasError
						? {
								outline: "2px solid var(--kendo-color-error)",
								borderRadius: "2px",
							}
						: undefined
				}
			>
				<DropDownList
					data={formattedOperators}
					dataItemKey={"value"}
					disabled={isDisabled}
					onChange={(e: DropDownListChangeEvent) => {
						const selected = e.value as RoutingGuideEnumItem;
						handleRowChange({
							dataItem,
							field: "operator" as keyof T,
							value: selected.name as T[keyof T],
						});
					}}
					textField={"label"}
					value={selectedOperator}
				/>
			</div>
			{hasError ? (
				<span
					style={{
						color: "var(--kendo-color-error)",
						display: "block",
						fontSize: "11px",
						marginTop: "2px",
					}}
				>
					A value must be input in order to activate
				</span>
			) : null}
		</GridTd>
	);
};
